/**
 * Pawn position type definitions for game state management
 *
 * These types are used to track pawn locations on the board during gameplay.
 * Position codes reference the centralized position system from the board constants.
 */

import type { BoardPosition } from '../components/board/constants/positions';

/**
 * Type alias for position codes (e.g., 'GS2', 'BS2', 'YC3', etc.)
 * References existing positions from apps/frontend/src/features/game/components/board/constants/positions.ts
 */
export type PawnPosition = BoardPosition;

/**
 * Type alias for pawn identifiers in the format `${playerId}-${pawnIndex}`
 * Example: 'player1-0', 'player2-3'
 * Used as keys in the pawnPositions Map in game store
 */
export type PawnId = string;

/**
 * Generate a standardized pawn ID from playerId and pawn index
 * @param playerId - Unique player identifier
 * @param pawnIndex - Pawn index (0-3)
 * @returns Pawn ID in format `${playerId}-${pawnIndex}`
 */
export const getPawnId = (playerId: string, pawnIndex: number): PawnId =>
  `${playerId}-${pawnIndex.toString()}`;

/**
 * Extract player ID from a pawn ID
 * Handles player IDs that contain dashes (e.g., "player-1-0" -> "player-1")
 * @param pawnId - Pawn ID in format `${playerId}-${pawnIndex}`
 * @returns The player ID portion
 * @example
 * getPlayerIdFromPawnId('player-1-0') // Returns 'player-1'
 * getPlayerIdFromPawnId('player1-2') // Returns 'player1'
 */
export const getPlayerIdFromPawnId = (pawnId: PawnId): string => {
  const lastDashIndex = pawnId.lastIndexOf('-');
  return pawnId.slice(0, lastDashIndex);
};

/**
 * Extract pawn index from a pawn ID
 * @param pawnId - Pawn ID in format `${playerId}-${pawnIndex}`
 * @returns The pawn index (0-3)
 * @example
 * getPawnIndexFromPawnId('player-1-0') // Returns 0
 * getPawnIndexFromPawnId('dev-user-abc-2') // Returns 2
 */
export const getPawnIndexFromPawnId = (pawnId: PawnId): number => {
  const lastDashIndex = pawnId.lastIndexOf('-');
  return parseInt(pawnId.slice(lastDashIndex + 1), 10);
};

/**
 * Mapping of player colors to their starting positions on the board
 * These are the positions where pawns are placed when released from base after rolling a 6
 *
 * Position codes reference the *S2 positions from the board constants:
 * - GS2: Green start position (bottom road section)
 * - BS2: Blue start position (top road section)
 * - YS2: Yellow start position (left road section)
 * - RS2: Red start position (right road section)
 */
export const START_POSITIONS = {
  green: 'GS2',
  blue: 'BS2',
  yellow: 'YS2',
  red: 'RS2'
} as const;

/**
 * Type for valid player colors
 */
export type PlayerColor = keyof typeof START_POSITIONS;
